/**
 * Super Admin › Tarifario. Lo que te cuesta y lo que cobras.
 *
 * LA PANTALLA PIDE UNA COSA Y ENSEÑA DOS
 * ======================================
 *
 * Arriba, los siete bloques: el dueño escribe su precio y **al lado ve cuánto
 * cuesta el caso más caro que cae dentro**. Es lo único que captura.
 *
 * Abajo, plegado, las líneas de costo real por proveedor y país. Están para
 * poder auditar de dónde salió ese «caso más caro», no para trabajar ahí.
 *
 * EL AVISO ES EL PRODUCTO
 * =======================
 *
 * Un bloque promedia. Con un solo precio para «plantilla de WhatsApp» se gana
 * con el cliente mexicano de utilidad y se pierde con el español de marketing,
 * que cuesta siete veces más y suele ser el que más manda. Sin el aviso al lado,
 * esta pantalla sería una forma cómoda de perder dinero sin enterarse.
 *
 * Avisa, no bloquea: hay razones comerciales legítimas para vender apretado.
 * El que sí se bloquea con el mínimo es el cotizador automático.
 */
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import FieldHelp from '@/components/help/FieldHelp';
import {
  Calculator, AlertTriangle, CheckCircle2, Info, ChevronDown, ChevronRight, Download,
} from 'lucide-react';

const COLOR_VEREDICTO = {
  bien: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10',
  apretado: 'text-amber-700 bg-amber-50 dark:bg-amber-500/10',
  pierde: 'text-red-700 bg-red-50 dark:bg-red-500/10',
  gratis: 'text-red-700 bg-red-50 dark:bg-red-500/10',
  sin_datos: 'text-[#64748B] bg-[#F8F9FA] dark:bg-white/5',
};

const TITULO_DIRECCION = {
  entrada: 'Lo que entra — captar clientes',
  salida: 'Lo que sale — perseguir clientes',
  inteligencia: 'La inteligencia',
};

export default function CostRates() {
  const [datos, setDatos] = useState(null);
  const [costos, setCostos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [verCostos, setVerCostos] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        api.get('/admin/tarifario'),
        api.get('/admin/tarifario/costos'),
      ]);
      setDatos(t.data);
      setCostos(c.data.costos || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) return <div className="p-8 text-sm text-[#64748B]">Cargando tarifario…</div>;
  if (!datos) return null;

  const porDireccion = {};
  (datos.bloques || []).forEach((b) => {
    (porDireccion[b.direccion] = porDireccion[b.direccion] || []).push(b);
  });
  const sinCostos = (datos.bloques || []).every((b) => !b.peor_caso);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="tarifario">
      <div>
        <h1 className="text-2xl font-bold">Tarifario</h1>
        <p className="text-sm text-[#64748B] mt-1 max-w-3xl">
          Lo que te cuesta atender a un cliente, y lo que le cobras por ello.
        </p>
      </div>

      {/* Por qué existe esta pantalla, dicho sin rodeos. */}
      <div className="rounded-lg border border-sky-200 bg-sky-50 dark:bg-sky-500/10 p-4 text-xs flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
        <span>
          <strong>Tú pones el precio de cada bloque; los costos no los pones tú.</strong>{' '}
          A un lado de cada bloque te digo cuánto cuesta el <em>caso más caro</em> que cae
          dentro, para que no lo promedies a ciegas: con un solo precio para «plantilla de
          WhatsApp» se gana con el cliente barato y se pierde con el caro, que suele ser el
          que más manda.
        </span>
      </div>

      {sinCostos && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-4"
             data-testid="sin-costos">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Todavía no hay costos cargados</p>
              <p className="text-xs text-[#64748B] mt-1">
                Sin ellos no puedo avisarte si un precio te deja perdiendo. Cargo los que ya
                conozco de las listas públicas de tus proveedores — vendrán marcados «sin
                verificar» hasta que los confirmes con tu factura.
              </p>
              <Button size="sm" className="mt-3" data-testid="cargar-conocidos"
                      onClick={async () => {
                        try {
                          const { data } = await api.post('/admin/tarifario/cargar-conocidos');
                          toast.success(`${data.creadas} costos cargados. ${data.aviso}`);
                          cargar();
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}>
                <Download className="w-3.5 h-3.5 mr-1.5" /> Cargar los costos que ya conocemos
              </Button>
            </div>
          </div>
        </div>
      )}

      <MargenMinimo valor={datos.margen_minimo} onGuardado={cargar} />

      {['entrada', 'salida', 'inteligencia'].map((dir) => (
        (porDireccion[dir] || []).length > 0 && (
          <div key={dir} className="space-y-3">
            <h2 className="text-sm font-semibold">{TITULO_DIRECCION[dir]}</h2>
            <div className="space-y-3">
              {porDireccion[dir].map((b) => (
                <Bloque key={b.clave} bloque={b} onGuardado={cargar} />
              ))}
            </div>
          </div>
        )
      ))}

      {/* La capa 1, plegada: está para auditar, no para trabajar ahí. */}
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10">
        <button onClick={() => setVerCostos(!verCostos)}
                className="w-full flex items-center gap-2 p-4 text-sm font-semibold text-left"
                data-testid="ver-costos-reales">
          {verCostos ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          De dónde salen esos costos ({costos.length} líneas)
        </button>
        {verCostos && <TablaDeCostos costos={costos} onCambio={cargar} />}
      </div>
    </div>
  );
}

function MargenMinimo({ valor, onGuardado }) {
  const [texto, setTexto] = useState(String(Math.round(parseFloat(valor || '0.30') * 100)));
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put('/admin/tarifario/politica', {
        margen_minimo: String((parseFloat(texto) || 0) / 100),
      });
      toast.success('Mínimo guardado');
      onGuardado?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4">
      <FieldHelp label="Tu margen mínimo" htmlFor="margen"
                 help={'Debajo de esto te aviso. A ti no te bloqueo —hay razones para vender apretado— '
                       + 'pero el cotizador automático sí se negará a cerrar por debajo, porque ese '
                       + 'vende sin ti enfrente.'}>
        <div className="flex items-center gap-2 max-w-xs">
          <Input id="margen" type="number" min="0" max="99" value={texto}
                 onChange={(e) => setTexto(e.target.value)} className="w-24" />
          <span className="text-sm text-[#64748B]">%</span>
          <Button size="sm" variant="outline" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </FieldHelp>
    </div>
  );
}

function Bloque({ bloque, onGuardado }) {
  const [precio, setPrecio] = useState(bloque.precio || '0');
  const [guardando, setGuardando] = useState(false);
  const [veredicto, setVeredicto] = useState(bloque.veredicto);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put(`/admin/tarifario/bloques/${bloque.clave}`, {
        precio, moneda: bloque.moneda || 'USD',
      });
      setVeredicto(data.veredicto);
      toast.success('Precio guardado');
      onGuardado?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const color = COLOR_VEREDICTO[veredicto?.estado] || COLOR_VEREDICTO.sin_datos;

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4"
         data-testid={`bloque-${bloque.clave}`}>
      <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-start">
        <div>
          <div className="text-sm font-semibold">{bloque.nombre}</div>
          <p className="text-xs text-[#64748B] mt-1">{bloque.ayuda}</p>
        </div>

        <div className="flex items-end gap-2">
          <FieldHelp label={`Cobras por ${bloque.unidad}`} htmlFor={`p-${bloque.clave}`}
                     help="Lo que le cargas a tu cliente por cada uno.">
            <div className="flex items-center gap-2">
              <Input id={`p-${bloque.clave}`} value={precio} onChange={(e) => setPrecio(e.target.value)}
                     className="w-28 tabular-nums" />
              <span className="text-xs text-[#64748B]">{bloque.moneda}</span>
            </div>
          </FieldHelp>
          <Button size="sm" variant="outline" onClick={guardar} disabled={guardando}>
            {guardando ? '…' : 'Guardar'}
          </Button>
        </div>

        <div className={`rounded-md p-3 text-xs ${color}`}>
          <div className="flex items-start gap-1.5">
            {veredicto?.estado === 'bien'
              ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            <span>{veredicto?.mensaje}</span>
          </div>
          {bloque.peor_caso && (
            <div className="mt-2 text-[11px] opacity-80">
              Caso más caro: {bloque.peor_caso.costo} {bloque.peor_caso.moneda}
              {bloque.peor_caso.pais ? ` en ${bloque.peor_caso.pais}` : ''}
              {bloque.peor_caso.estimado ? ' (estimado)' : ''}
            </div>
          )}
          {bloque.hay_sin_verificar && (
            <div className="mt-1 text-[11px] opacity-80">
              Algunos costos de este bloque están sin verificar.
            </div>
          )}
        </div>
      </div>

      {bloque.aviso_dispersion && (
        <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 dark:bg-amber-500/10 rounded p-2">
          {bloque.aviso_dispersion}
        </p>
      )}
    </div>
  );
}

function TablaDeCostos({ costos, onCambio }) {
  if (!costos.length) {
    return <p className="px-4 pb-4 text-xs text-[#64748B]">Todavía no hay costos cargados.</p>;
  }
  return (
    <div className="px-4 pb-4 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[#64748B] border-b border-[#E2E8F0] dark:border-white/10">
            <th className="py-2">Bloque</th>
            <th className="py-2">Proveedor</th>
            <th className="py-2">Concepto</th>
            <th className="py-2">País</th>
            <th className="py-2 text-right">Costo</th>
            <th className="py-2">Estado</th>
          </tr>
        </thead>
        <tbody>
          {costos.map((c) => (
            <tr key={c.id} className="border-b border-[#E2E8F0] dark:border-white/10 last:border-0">
              <td className="py-2">{c.bloque_nombre}</td>
              <td className="py-2">{c.proveedor}</td>
              <td className="py-2">{c.concepto}</td>
              <td className="py-2">{c.pais || 'todos'}</td>
              <td className="py-2 text-right tabular-nums">{c.costo_unitario} {c.moneda}</td>
              <td className="py-2">
                {c.verificado
                  ? <span className="text-emerald-700">verificado</span>
                  : (
                    <button className="text-amber-700 underline"
                            onClick={async () => {
                              try {
                                await api.put(`/admin/tarifario/costos/${c.id}/verificar`);
                                toast.success('Marcado como verificado');
                                onCambio?.();
                              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                            }}>
                      sin verificar — confirmar
                    </button>
                  )}
                {c.estimado && <span className="ml-2 text-[#64748B]">(estimado)</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
